'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { InstructorDetails } from '@udemy-v2/browse-course';
import styles from './instructor-display.module.scss';
import classNames from 'classnames';
import { useI18n, LocalizedHtml } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { generateInstructorAnchorHash } from '../instructor-module/utils/instructor-utils';
import { Icon } from '@udemy-v2/icons';
import { useInstructorCommunityBannerInfo } from '@/lib/hooks/use-instructor-community-banner-info';
import { useInstructorDetails } from '@/lib/hooks/use-instructor-details';
import { CommunityPoweredBadge } from '../instructor-community/community-powered-badge';
interface InstructorDisplayProps {
  instructors: InstructorDetails[];
  isAGwGCourse?: boolean;
}

/**
 * Displays course instructors with links to their profiles
 *
 * @example
 * ```jsx
 * <InstructorDisplay instructors={course.instructors} />
 * ```
 */
export function InstructorDisplay(t0) {
  const $ = _c(24);
  const {
    instructors,
    isAGwGCourse: t1
  } = t0;
  const isAGwGCourse = t1 === undefined ? false : t1;
  const {
    gettext,
    interpolate
  } = useI18n();
  const firstInstructor = instructors?.[0];
  const {
    hasCommunity
  } = useInstructorCommunityBannerInfo(firstInstructor?.id);
  const instructorDetails = useInstructorDetails();
  if (!instructors?.length) {
    return null;
  }
  const showCommunityPoweredBadge = !isAGwGCourse && hasCommunity;
  let t2;
  if ($[0] !== firstInstructor?.id || $[1] !== instructorDetails) {
    let t3;
    if ($[3] !== firstInstructor?.id) {
      t3 = instructor => instructor.id === firstInstructor?.id;
      $[3] = firstInstructor?.id;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = instructorDetails.find(t3);
    $[0] = firstInstructor?.id;
    $[1] = instructorDetails;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const firstInstructorDetails = t2;
  const firstInstructorImageUrl = firstInstructorDetails?.images.large ?? firstInstructor?.images.small;
  const handleOnInstructorNameClick = _temp;
  let t3;
  if ($[5] !== firstInstructor || $[6] !== firstInstructorImageUrl || $[7] !== showCommunityPoweredBadge) {
    t3 = () => {
      if (!showCommunityPoweredBadge || !firstInstructorImageUrl) {
        return null;
      }
      return <div className={styles["instructor-header-leading"]} data-purpose="instructor-header-image"><div className={styles["instructor-header-image-container"]}><CommunityPoweredBadge imageUrl={firstInstructorImageUrl} displayName={firstInstructor.name} url={firstInstructor.url} size="sm" /></div></div>;
    };
    $[5] = firstInstructor;
    $[6] = firstInstructorImageUrl;
    $[7] = showCommunityPoweredBadge;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderInstructorHeaderImage = t3;
  let t4;
  if ($[9] !== gettext || $[10] !== instructors || $[11] !== interpolate || $[12] !== isAGwGCourse || $[13] !== showCommunityPoweredBadge) {
    t4 = () => {
      const Names = () => {
        if (isAGwGCourse) {
          return <Button componentClass="a" udStyle="link" className={classNames("ud-text-sm", styles["instructor-link"])} onClick={handleOnInstructorNameClick} href={generateInstructorAnchorHash(0)}>Google</Button>;
        }
        const instructorButtons = instructors.map((instructor_0, i) => <Button componentClass="a" udStyle="link" key={instructor_0.id} className={classNames("ud-text-sm", styles["instructor-link"])} onClick={handleOnInstructorNameClick} data-position={i + 1} href={instructor_0.url || generateInstructorAnchorHash(i)}>{instructor_0.name}</Button>);
        return instructorButtons.reduce(_temp2, []);
      };
      return <span className={classNames(styles["instructor-container"], showCommunityPoweredBadge && styles.names)}>{isAGwGCourse && <Icon glyph="google" size="xsmall" label={false} className={styles["google-icon"]} />}<LocalizedHtml html={interpolate(gettext("<span class=\"created-by\">Created by</span>"), true)} interpolate={{
          "created-by": <span className={styles["created-by"]} />
        }} /><Names /></span>;
    };
    $[9] = gettext;
    $[10] = instructors;
    $[11] = interpolate;
    $[12] = isAGwGCourse;
    $[13] = showCommunityPoweredBadge;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const renderInstructorNames = t4;
  if (!showCommunityPoweredBadge) {
    let t5;
    if ($[15] !== renderInstructorNames) {
      t5 = renderInstructorNames();
      $[15] = renderInstructorNames;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    return t5;
  }
  let t5;
  if ($[17] !== renderInstructorHeaderImage) {
    t5 = renderInstructorHeaderImage();
    $[17] = renderInstructorHeaderImage;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== renderInstructorNames) {
    t6 = renderInstructorNames();
    $[19] = renderInstructorNames;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <div className={styles["with-community-badge"]} data-purpose="instructor-name-top">{t5}{t6}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp2(acc, curr, index) {
  if (index === 0) {
    return [curr];
  }
  return [...acc, ", ", curr];
}
function _temp(event) {
  const target = event.target as HTMLElement;
  const parentElement = target.parentElement as HTMLAnchorElement | null;
  const position = parentElement?.dataset?.position || "1";
  const id = generateInstructorAnchorHash(parseInt(position, 10) - 1);
  if (parentElement) {
    parentElement.href = `${window.location.search}${id}`;
  }
}